import { AxiosResponse } from 'axios'
import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { $axios } from './axios'
import { InboxMemberType } from '../../server/models/InboxMember'

export const createInboxMember = (
  inboxId: string,
): Promise<AxiosResponse<InboxMemberType>> => {
  return $axios.post(`/inboxes/${inboxId}/members`)
}

export const getInboxMembers = (
  inboxId: string,
): Promise<AxiosResponse<InboxMemberType[]>> => {
  return $axios.get(`/inboxes/${inboxId}/members`)
}

export const countInboxMembers = (
  inboxId: string,
): Promise<AxiosResponse<{ count: number }>> => {
  return $axios.get(`/inboxes/${inboxId}/members/count`)
}

export const useInboxMembersCountQuery = (
  inboxId: string,
  options?: Omit<UseQueryOptions<number, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<number, Error>({
    queryKey: ['inboxMembersCount', inboxId],
    queryFn: async () => {
      const res = await countInboxMembers(inboxId)
      return res.data.count
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - liberal caching
    gcTime: 60 * 60 * 1000, // 1 hour - keep in cache for a long time
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    ...options
  })
}

export const getInboxesOfUser = (
  userId: string
): Promise<AxiosResponse<InboxMemberType[]>> => {
  return $axios.get(`/user/${userId}/inboxes`)
}

export const useInboxesOfUserQuery = (
  userId: string,
  options?: Omit<UseQueryOptions<InboxMemberType[], Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<InboxMemberType[], Error>({
    queryKey: ['inboxesOfUser', userId],
    queryFn: async () => {
      const res = await getInboxesOfUser(userId)
      return res.data
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - liberal caching
    gcTime: 60 * 60 * 1000, // 1 hour - keep in cache for a long time
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    ...options
  })
}

export const deleteInboxMember = (
  inboxId: string,
  userId: string
): Promise<AxiosResponse<void>> => {
  return $axios.delete(`/inboxes/${inboxId}/members/${userId}`)
}

export {
  InboxMemberType
}
